import { useEffect } from 'react';
import { $generateNodesFromDOM } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, $insertNodes } from 'lexical';

import { $convertImageElement } from '../../nodes/ImageNode';

type HTMLCompatibilityPluginProps = {
  initialContent?: string;
};

/**
 * Checks if a string is likely HTML content
 */
const isHTMLContent = (content: string): boolean => {
  // If it's empty or not a string, it's not HTML
  if (!content || typeof content !== 'string') return false;

  // Try parsing as JSON first - if it succeeds, it's likely Lexical JSON
  try {
    JSON.parse(content);
    return false;
  } catch {
    // If JSON parsing fails, check for HTML tags
    const containsHTMLTags = /<[a-z][\s\S]*>/i.test(content);
    return containsHTMLTags;
  }
};

export function HTMLCompatibilityPlugin({ initialContent }: HTMLCompatibilityPluginProps) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!initialContent) return;

    // Only process if content is HTML
    if (!isHTMLContent(initialContent)) return;

    // Convert HTML to Lexical nodes
    const parser = new DOMParser();
    const dom = parser.parseFromString(initialContent, 'text/html');

    // NOTE: We should ideally register the conversion for 'img' elements using importDOM method provided by lexical.DecoratorNode
    // However, the LexicalRichTextPlugin runs on paste, and uses $generateNodesFromDOM to convert HTML to Lexical nodes.
    // We also have our own paste handler that uploads the pasted images to our own server and inserts them as Lexical nodes.
    // The result is that images are inserted twice, once by the rich text plugin's paste handler and once by our custom paste handler.
    // We also need to have the functionality to import images from html strings, to maintain compatibility with existing prosemirror content.
    // To avoid the double paste, we register a custom conversion for 'img' elements here, and remove it once we are done importing prosemirror content,
    // so that it does not get in the way of rich text plugin's paste handler.
    if (!editor._htmlConversions.has('img')) {
      editor._htmlConversions.set('img', []);
    }
    editor._htmlConversions.get('img')?.push(() => ({
      conversion: $convertImageElement,
      priority: 0,
    }));

    editor.update(() => {
      const nodes = $generateNodesFromDOM(editor, dom);
      const root = $getRoot();
      root.clear();
      $insertNodes(nodes);
    });

    // Clean up the custom conversion after use
    editor._htmlConversions.get('img')?.pop();
  }, [editor, initialContent]);

  return null;
}
